import React from 'react';

import { FileType } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNACard, DNAText, DNAButton, useTheme, Iffy } from '@alucio/lux-ui';
import {
  DNAChip,
  DNACustomFieldChipList,
  DNADocumentThumbnail,
  DNADocumentContextMenu,
  DNARowsComponent,
  useDNADocumentActions,
} from 'src/components/DNA';
import { DocumentORM } from 'src/types/types';
import { CustomLabelDisplay } from '../default/CustomLabelDisplay';
import { useAppSettings } from 'src/state/context/AppSettings';

export type DNADocumentDefaultRowProps = {
  onPin?: () => void,
  isOnline?: boolean,
}

const DNADocumentSearchRow: DNARowsComponent<
  DocumentORM,
  DNADocumentDefaultRowProps
> = (props) => {
  const {
    ORM: documentORM,
    isOnline,
    onPin,
    onPress,
    style,
  } = props

  const theme = useTheme()

  const isBookmarked = documentORM.meta.bookmark.isBookmarked
  const documentActions = useDNADocumentActions();
  const { deviceMode } = useAppSettings();
  const unavailableContent =
    (!isOnline &&
    !documentORM.relations.version.cachedDocumentVersionORM?.meta.assets.isContentCached) ||
    (documentORM.model.type === FileType.WEB && !isOnline)
  const title = documentORM.relations.version.latestPublishedDocumentVersionORM?.model.title

  return (
    <DNACard
      appearance="flat"
      interactive="pressable"
      onPress={() => { onPress?.(documentORM) }}
      style={[
        {
          paddingHorizontal: deviceMode === 'desktop' ? theme['row-padding-horizontal'] : 29,
          paddingVertical: theme['row-padding-vertical'],
        },
        style,
      ]}
    >
      <DNABox alignY="center" spacing="md" childFill={1}>
        {/* Actions */}
        <DNABox spacing={deviceMode === 'desktop' ? 'md' : 'lg'}>
          <DNABox style={{ width: 22 }}>
            <DNAButton
              appearance="ghost"
              status={isBookmarked ? 'primary' : 'subtle'}
              size={deviceMode === 'desktop' ? 'md' : 'xl'}
              iconLeft={isBookmarked ? 'star-fill' : 'star'}
              onPress={() => {
                documentActions.bookmark(documentORM)()
                onPin?.()
              }}
            />
          </DNABox>
          <DNADocumentContextMenu documentORM={documentORM}>
            <DNAButton
              appearance="ghost"
              status="subtle"
              size={deviceMode === 'desktop' ? 'md' : 'xl'}
              iconLeft="dots-three-vertical-bold"
            />
          </DNADocumentContextMenu>
        </DNABox>
        <DNABox alignY="center" spacing="lg" childFill={1} style={{ opacity: unavailableContent ? 0.4 : 1 }}>
          <DNADocumentThumbnail.Hover
            documentVersionORM={
              (!isOnline && documentORM.relations.version.cachedDocumentVersionORM) ||
              documentORM.relations.version.latestPublishedDocumentVersionORM ||
              documentORM.relations.version.latestDocumentVersionORM
            }
            width={120}
            height={68}
            unavailableContent={unavailableContent}
            // Assessing implementation in #BEAC-2155
            // onPress={() => { console.log('pressed search') }}
          />
          {/* Title */}
          <DNABox
            spacing="sm"
            appearance="col"
            childFill
            style={{ paddingRight: 16 }}
          >
            <DNAText b1 numberOfLines={1}>{title}
            </DNAText>
            <DNABox alignY="center" spacing="sm" childFill={unavailableContent ? 2 : 1}>
              <DNACustomFieldChipList
                documentVersionORM={documentORM.relations.version.latestUsableDocumentVersionORM}
              />
              <Iffy is={unavailableContent}>
                <DNAChip.Beacon
                  testID={`content-not-available-${title}`}
                  variant="CONTENT_UNAVAILABLE"
                />
              </Iffy>
              <CustomLabelDisplay documentORM={documentORM} />
            </DNABox>
          </DNABox>
        </DNABox>
      </DNABox>
    </DNACard>
  )
}

export default DNADocumentSearchRow
